import type {UserRole} from '../../types/enums';
import type {ProjectStatus} from '../../types/projectStatus';

/**
 * Checks user role against given role and target role
 * @param userRole user role
 * @returns role check function
 */
export const roleTargetCheck =
  (role: UserRole) => (user: {roleId: UserRole}, target: {roleId: UserRole}) =>
    user.roleId >= role && target.roleId < user.roleId;

/**
 * Checks user role against given role and asset permissions
 * @param userRole user role
 * @returns role asset check function
 */
export const roleAssetCheck =
  (role: UserRole) =>
  (
    user: {roleId: UserRole; id: string},
    asset: {
      memberIds: string[];
      uploaderIds: string[];
      createdBy: {id: string} | null;
    }
  ) =>
    user.roleId >= role ||
    !!asset.memberIds.includes(user.id) ||
    !!asset.uploaderIds.includes(user.id) ||
    asset.createdBy?.id === user.id;

/**
 * Checks user role against given role, asset permissions, and project status
 * @param userRole user role
 * @returns role project check function
 */
export const roleProjectStatusCheck =
  (role: UserRole) =>
  (
    user: {roleId: UserRole; id: string},
    projectStatus: ProjectStatus,
    asset: {
      memberIds: string[];
      uploaderIds: string[];
      createdBy: {id: string} | null;
    }
  ) =>
    user.roleId >= role ||
    (projectStatus === 'pending' &&
      (!!asset.memberIds.includes(user.id) ||
        !!asset.uploaderIds.includes(user.id) ||
        asset.createdBy?.id === user.id));

/**
 * Checks user role against given role and if user is asset member
 * @param userRole user role
 * @returns role asset member check function
 */
export const roleAssetMemberCheck =
  (role: UserRole) =>
  (user: {roleId: UserRole; id: string}, asset: {memberIds: string[]}) =>
    user.roleId >= role || asset.memberIds.includes(user.id);

/**
 * Checks user role against given role and if user is asset member
 * @param userRole user role
 * @returns role asset member check function
 */
export const linkPermissionCheck =
  (target: UserRole) => (roleId: UserRole, permission: boolean | undefined) =>
    roleId >= target && (typeof permission === 'undefined' || permission);

/**
 * Checks user role against given role and if target is self
 * @param userRole user role
 * @returns role self check function
 */
export const roleSelfCheck =
  (role: UserRole) =>
  (
    user: {roleId: UserRole; id: string},
    target: {roleId: UserRole; id: string}
  ) =>
    (user.roleId >= role && user.roleId > target.roleId) ||
    user.id === target.id;

/**
 * Checks user is same as target
 * @returns self check function
 */
export const selfCheck = (user: {id: string}, target: {id: string}) =>
  user.id === target.id;

// const checkPermissionMap = (
//   action: ViewerPermissions,
//   permissions: PermissionState
// ): boolean => {
//   switch (action) {
//     case ViewerPermissions.Download: {
//       return permissions.hasDownload;
//     }

//     case ViewerPermissions.ViewMap: {
//       return permissions.hasMap;
//     }
//   }
// };

// export const checkViewerPermissions = (
//   permissions: PermissionState | null,
//   action: ViewerPermissions,
//   user: {role: UserRole}
// ) =>
//   // full users shouldn't have this object
//   !permissions ||
//   // not doing >= is extra check for full users
//   // should be changed to >= if per-user permissions expand to full users

//   user.role > action ||
//   // check against db permission values
//   checkPermissionMap(action, permissions);
